import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import ButtonRow from "./ButtonRow";
import CustomSplitColumnCard from "../../misc/CustomSplitColumnCard";
import MissionForm from "../../Room/Missions/MissionForm";
import Mission from "../../../interfaces/robot/missions/mission";
import CustomSnackbar from "../../Feedback/CustomSnackbar";
import { selectRobotCleaningCommandButtons } from "../../../store/robot-configurations/robot-configurations-selectors";
import { selectMissionsData } from "../../../store/room/room-selectors";
import HttpFetchStatus from "../../../enums/api/http-fetch-status";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../store/SharedProvider";
import {
  errorResetResponse,
  isErrorSetOpen,
  isSuccessSetOpen,
  successResetResponse,
} from "../../../interfaces/api/rest-set";
import { ButtonsIcon } from "../../../utils/icons/icons";
import Divider from "@mui/material/Divider";

const ButtonConfigurationCard: React.FC = () => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();

  const { getButtonConfiguration, getGlobalMissions, patchMissionsRestSet } = useSharedActions();

  const cleaningCommandButtons = useSharedSelector((state) => selectRobotCleaningCommandButtons(state));
  const missions = useSharedSelector((state) => selectMissionsData(state));

  const [editingMission, setEditingMission] = useState<Mission | null>(null);

  useEffect(() => {
    if (cleaningCommandButtons.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getButtonConfiguration());
    }
  }, [dispatch, getButtonConfiguration, cleaningCommandButtons.rest]);

  useEffect(() => {
    if (missions.rest.getStatus === HttpFetchStatus.Idle) {
      dispatch(getGlobalMissions());
    }
  }, [dispatch, getGlobalMissions, missions.rest]);

  const configButtonClickHandler = (mission?: Mission) => {
    if (mission != null) {
      setEditingMission(mission);
    }
  };

  const closeMissionFormHandler = () => {
    setEditingMission(null);
  };

  const resetMissionsStateHandler = () => {
    if (isSuccessSetOpen(missions.rest)) {
      dispatch(patchMissionsRestSet(successResetResponse(missions.rest)));
    } else if (isErrorSetOpen(missions.rest)) {
      dispatch(patchMissionsRestSet(errorResetResponse(missions.rest)));
    }
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t("configuration-buttons")}
        content={[
          {
            left: (
              <Stack spacing={1} pr={2}>
                <Stack direction="row" alignItems="center" spacing={1} py={1}>
                  <ButtonsIcon />
                  <Typography>{t("robot-command-buttons")}</Typography>
                </Stack>
              </Stack>
            ),
            right: (
              <Stack spacing={2} pb={1} divider={<Divider flexItem />}>
                {cleaningCommandButtons.data === null || missions.globalMissions.length === 0 ? (
                  <React.Fragment>
                    {[1, 2, 3, 4, 5, 6].map((index) => (
                      <Stack direction="row" key={index}>
                        <Stack flexGrow={1}>
                          <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(3), width: "20%" })} />
                          <Skeleton variant="text" sx={(theme) => ({ height: theme.spacing(4), width: "50%" })} />
                        </Stack>
                        <Stack alignItems="center" spacing={1} direction="row">
                          <Skeleton
                            variant="circular"
                            sx={(theme) => ({ height: theme.spacing(5), width: theme.spacing(5) })}
                          />
                        </Stack>
                      </Stack>
                    ))}
                  </React.Fragment>
                ) : (
                  cleaningCommandButtons.data.map((button) => {
                    const mission = missions.globalMissions.find((m) => m.id === button.missionId);
                    return (
                      <ButtonRow
                        key={button.missionId}
                        cleaningButton={button}
                        onClick={() => configButtonClickHandler(mission)}
                        mission={mission}
                      />
                    );
                  })
                )}
              </Stack>
            ),
          },
        ]}
      />
      {editingMission != null && (
        <MissionForm
          open={editingMission != null}
          onClose={closeMissionFormHandler}
          mission={editingMission}
          navigatorAsName
        />
      )}
      <CustomSnackbar
        title={t("success-saving-mission")}
        open={missions.rest.patchStatus === HttpFetchStatus.Success}
        severity="success"
        onClose={resetMissionsStateHandler}
      />
      <CustomSnackbar
        title={t("error-saving-mission")}
        open={missions.rest.patchStatus === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetMissionsStateHandler}
      />
    </React.Fragment>
  );
};

export default ButtonConfigurationCard;
